import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactLoading from "react-loading";
import { useTranslation } from "react-i18next";
import arrowUpIcon from "../../../../assets/images/mgsuite/Arrow_Up_Icon.svg";
import textDisplayIcon from "../../../../assets/images/mgsuite/Text_Display_Icon.png";

function read(model, ...names) {
  for (let i = 0; i < names.length; i += 1) {
    if (model && model[names[i]] !== undefined) return model[names[i]];
  }
  return undefined;
}

function collection(model, camelName, pascalName) {
  const value = read(model, camelName, pascalName);
  return Array.isArray(value) ? value : [];
}

function rgbToHex(model, fallback) {
  const hex = read(model, "colorHEX", "ColorHEX", "colorHex", "ColorHex");
  if (hex) return hex.startsWith("#") ? hex : `#${hex}`;

  const r = read(model, "colorR", "ColorR");
  const g = read(model, "colorG", "ColorG");
  const b = read(model, "colorB", "ColorB");
  if ([r, g, b].some((value) => value === undefined)) return fallback;
  return `#${[r, g, b]
    .map((value) => Number(value).toString(16).padStart(2, "0"))
    .join("")}`;
}

function base64Source(value) {
  if (!value) return null;
  if (String(value).startsWith("data:")) return value;
  return `data:image/png;base64,${value}`;
}

function normalizeImageName(value) {
  return String(value || "")
    .split(/[\\/]/)
    .pop()
    .replace(/\.[^.]+$/, "")
    .trim()
    .toLowerCase();
}

function textState(display, states, configType, fallbackText, fallbackColor) {
  const displayId = read(display, "id", "Id");
  const state =
    states.find((item) => {
      const ownerId = read(
        item,
        "textDisplayId",
        "TextDisplayId",
        "displayId",
        "DisplayId",
        "elementId",
        "ElementId",
      );
      return (
        ownerId === displayId &&
        Number(read(item, "configType", "ConfigType")) === configType
      );
    }) ||
    states.find(
      (item) =>
        Number(read(item, "configType", "ConfigType")) === configType,
    ) ||
    {};

  const fonts = [
    "Arial",
    "Arial Narrow",
    "Courier New",
    "Tahoma",
    "Times New Roman",
    "Verdana",
  ];
  const fontId = Number(read(state, "fontId", "FontId") || 0);
  const orientationValue = read(state, "orientation", "Orientation");
  const orientation = Number(
    orientationValue === undefined || orientationValue === null
      ? 1
      : orientationValue,
  );

  return {
    configType,
    mode:
      Number(read(state, "displayMode", "DisplayMode") || 0) === 0
        ? "count"
        : "text",
    text: read(state, "text", "Text") || fallbackText,
    font: fonts[fontId] || "Arial",
    fontId,
    size: Number(read(state, "fontSize", "FontSize") || 12),
    color: rgbToHex(state, fallbackColor),
    align: ["left", "center", "right"][orientation] || "center",
    orientation,
  };
}

export function normalizeTextDisplaySettings(display, states) {
  const backendId = read(display, "id", "Id");

  return {
    raw: display,
    backendId,
    activeConfigType: Number(
      read(
        display,
        "currentlyVisibleDisplayConfigType",
        "CurrentlyVisibleDisplayConfigType",
        "currentDisplayConfigType",
        "CurrentDisplayConfigType",
      ) || 1,
    ),
    currentDisplayValue: read(
      display,
      "displayValueString",
      "DisplayValueString",
      "currentDisplayValue",
      "CurrentDisplayValue",
    ),
    free: {
      ...textState(display, states, 1, "FREE", "#00ff00"),
      count: read(
        display,
        "currentFreeValue",
        "CurrentFreeValue",
        "freeValue",
        "FreeValue",
      ),
    },
    occupied: {
      ...textState(display, states, 2, "BUSY", "#ff0000"),
      count: read(
        display,
        "currentOccupiedValue",
        "CurrentOccupiedValue",
        "occupiedValue",
        "OccupiedValue",
      ),
    },
  };
}

function normalizeModel(model) {
  const sign = read(model, "sign", "Sign") || {};
  const connectedDevices = collection(
    sign,
    "connectedDevices",
    "ConnectedDevices",
  );
  const textDisplays = collection(model, "textDisplays", "TextDisplays");
  const textStates = collection(
    model,
    "textDisplayStateConfigs",
    "TextDisplayStateConfigs",
  );
  const arrowDisplays = collection(model, "arrowDisplays", "ArrowDisplays");
  const imageDisplays = collection(model, "imageDisplays", "ImageDisplays");
  const elements = [];
  const settings = {};

  textDisplays.forEach((display) => {
    const backendId = read(display, "id", "Id");
    const id = `text-${backendId}`;
    elements.push({ id, backendId, type: "text", model: display });
    settings[id] = normalizeTextDisplaySettings(display, textStates);
  });

  arrowDisplays.forEach((display) => {
    const backendId = read(display, "id", "Id");
    const id = `arrow-${backendId}`;
    elements.push({ id, backendId, type: "arrow", model: display });
    const freeConfig =
      read(display, "displayConfigForFree", "DisplayConfigForFree") || {};
    const occupiedConfig =
      read(display, "displayConfigForOccupied", "DisplayConfigForOccupied") ||
      {};
    settings[id] = {
      raw: display,
      backendId,
      image: base64Source(
        read(
          display,
          "stateImage",
          "StateImage",
          "base64PNG",
          "Base64PNG",
          "image",
          "Image",
        ),
      ),
      free: {
        configType: read(freeConfig, "configType", "ConfigType"),
        angle: Number(read(freeConfig, "rotation", "Rotation") || 0),
        color: rgbToHex(freeConfig, "#00ff00"),
      },
      occupied: {
        configType: read(occupiedConfig, "configType", "ConfigType"),
        angle: Number(read(occupiedConfig, "rotation", "Rotation") || 0),
        color: rgbToHex(occupiedConfig, "#ff0000"),
      },
    };
  });

  imageDisplays.forEach((display) => {
    const backendId = read(display, "id", "Id");
    const id = `icon-${backendId}`;
    const imageType = read(
      display,
      "elementTypeNameDetailed",
      "ElementTypeNameDetailed",
    );
    const imagesByType =
      read(
        model,
        "imageDisplayDefaultImagesByDisplayType",
        "ImageDisplayDefaultImagesByDisplayType",
      ) || {};
    const defaultImages =
      collection(display, "defaultImages", "DefaultImages").length > 0
        ? collection(display, "defaultImages", "DefaultImages")
        : imagesByType[imageType] || [];
    const freeConfig =
      read(display, "displayConfigForFree", "DisplayConfigForFree") || {};
    const [currentIconName, currentColor] = String(
      read(display, "currentDisplayValue", "CurrentDisplayValue") || "",
    ).split(";");
    const currentImage = defaultImages.find(
      (image) =>
        normalizeImageName(read(image, "name", "Name")) ===
        normalizeImageName(currentIconName),
    );
    elements.push({ id, backendId, type: "icon", model: display });
    settings[id] = {
      raw: display,
      backendId,
      defaultImages,
      configType: read(freeConfig, "configType", "ConfigType"),
      icon:
        read(currentImage, "name", "Name") ||
        currentIconName ||
        read(freeConfig, "includedIconName", "IncludedIconName") ||
        read(defaultImages[0], "name", "Name") ||
        "",
      color: currentColor || rgbToHex(freeConfig, "#ffffff"),
    };
  });

  const connectedDeviceOrder = new Map();
  connectedDevices.forEach((device, index) => {
    const deviceId =
      typeof device === "object"
        ? read(device, "id", "Id", "elementId", "ElementId")
        : device;

    if (deviceId !== undefined && deviceId !== null) {
      connectedDeviceOrder.set(String(deviceId), index);
    }
  });

  elements.sort((first, second) => {
    const firstIndex = connectedDeviceOrder.get(String(first.backendId));
    const secondIndex = connectedDeviceOrder.get(String(second.backendId));
    const firstOrder = firstIndex === undefined ? Infinity : firstIndex;
    const secondOrder = secondIndex === undefined ? Infinity : secondIndex;

    return firstOrder - secondOrder;
  });

  return {
    elements,
    settings,
    backgroundImageId:
      read(
        sign,
        "backgroundImageId",
        "BackgroundImageId",
      ) || "",
  };
}

const SmartSignSettingsMonitorComponents = (props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const link = localStorage.getItem("link");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);

    axios
      .get(`${link}/api/SmartSignage/FullModelById?id=${props.data.id}`)
      .then((response) => {
        if (!active) return;
        const normalized = normalizeModel(response.data);
        props.onLoaded(normalized);
        if (!props.selectedId && normalized.elements.length) {
          props.onSelect(normalized.elements[0]);
        }
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [link, props.data.id, props.refreshKey]);

  function renderElement(element) {
    const config = props.settings[element.id];
    if (!config) return null;

    if (element.type === "text") {
      return (
        <img
          src={textDisplayIcon}
          alt={t("smartSign.textDisplay")}
        />
      );
    }

    if (element.type === "arrow") {
      return (
        <img
          src={arrowUpIcon}
          alt=""
          style={{
            transform: `rotate(${config.free.angle}deg)`,
          }}
        />
      );
    }

    const image = config.defaultImages.find(
      (item) =>
        normalizeImageName(read(item, "name", "Name")) ===
        normalizeImageName(config.icon),
    );
    const source = base64Source(
      read(
        image,
        "image",
        "Image",
        "base64PNG",
        "Base64PNG",
        "data",
        "Data",
      ),
    );
    return source ? <img src={source} alt="" /> : <span>▧</span>;
  }

  function tooltip(element) {
    const config = props.settings[element.id];
    if (element.type !== "text" || !config) {
      return read(element.model, "name", "Name") || "";
    }

    const state =
      Number(config.activeConfigType) === 2 ? config.occupied : config.free;
    if (state.mode === "text") return state.text;

    const count =
      state.count !== undefined && state.count !== null
        ? state.count
        : config.currentDisplayValue;
    return count !== undefined && count !== null
      ? String(count)
      : t("smartSign.occupancyCount");
  }

  return (
    <div className="settings-groups">
      <div className="settings-name">{t("smartSign.monitorComponents")}</div>
      <div className="smart-sign-components">
        {loading && (
          <ReactLoading type="bars" color="#2a3042" height={45} width={45} />
        )}
        {!loading && error && <span>{t("smartSign.monitorLoadError")}</span>}
        {!loading &&
          !error &&
          props.elements.map((element) => (
            <button
              type="button"
              key={element.id}
              title={tooltip(element)}
              className={`smart-sign-component ${
                props.selectedId === element.id ? "selected" : ""
              }`}
              onClick={() => props.onSelect(element)}
            >
              {renderElement(element)}
            </button>
          ))}
        {!loading && !error && !props.elements.length && (
          <span>{t("smartSign.noMonitorComponents")}</span>
        )}
      </div>
    </div>
  );
};

export default SmartSignSettingsMonitorComponents;
